import type { GpuContext } from '../context.js';
import { workgroupCount } from '../context.js';
import type { GpuImage } from '../resources.js';
import { Struct } from '../uniform-writer.js';
import { COMBINE_PREMULTIPLY, COMBINE_RGB_ALPHA_A, combine } from './combine.js';

export interface AlphaFillOptions {
  /** Pyramid depth; defaults to enough levels to reach roughly 4x4. */
  levels?: number;
}

/**
 * Extends the artwork's colour outwards into transparent regions (pull-push
 * inpainting), keeping the original alpha.
 *
 * Run this between matting and super-resolution. Under transparent pixels the
 * RGB is still whatever the mockup's garment was; an SR model sees that as real
 * signal, sharpens the garment weave right up against the artwork edge, and the
 * result bleeds back into the visible pixels when the matte is re-applied.
 * Filling with the artwork's own colour first makes the model's job trivial
 * there and leaves edges clean.
 */
export function alphaFill(
  ctx: GpuContext,
  src: GpuImage,
  dst: GpuImage,
  options: AlphaFillOptions = {},
): void {
  const maxLevels = Math.floor(Math.log2(Math.max(2, Math.min(src.width, src.height)))) - 1;
  const levels = Math.max(1, Math.min(options.levels ?? maxLevels, maxLevels));

  // Level 0 holds (rgb * a, a) - the premultiplied invariant every pull/push
  // step preserves.
  const pyramid: GpuImage[] = [ctx.image(src.width, src.height, 'rgba16float', 'alphafill-0')];
  combine(ctx, src, src, pyramid[0]!, COMBINE_PREMULTIPLY);

  for (let level = 1; level <= levels; level++) {
    const parent = pyramid[level - 1]!;
    const width = Math.max(1, parent.width >> 1);
    const height = Math.max(1, parent.height >> 1);
    const child = ctx.image(width, height, 'rgba16float', `alphafill-${level}`);
    ctx.dispatch(
      {
        module: 'alphafill_pull.wgsl',
        bindings: [
          { kind: 'texture', format: 'rgba16float' },
          { kind: 'storageTexture', format: 'rgba16float' },
          { kind: 'uniform' },
        ],
      },
      [
        parent,
        child,
        ctx.uniform(new Struct(16).i(0, parent.width, parent.height).i(8, width, height).bytes),
      ],
      [workgroupCount(width, 8), workgroupCount(height, 8)],
      `alphafill:pull#${level}`,
    );
    pyramid.push(child);
  }

  // Push back down, unpremultiplying only at the finest level.
  for (let level = levels - 1; level >= 0; level--) {
    const fine = pyramid[level]!;
    const coarse = pyramid[level + 1]!;
    const out = ctx.image(fine.width, fine.height, 'rgba16float', `alphafill-push-${level}`);
    ctx.dispatch(
      {
        module: 'alphafill_push.wgsl',
        bindings: [
          { kind: 'texture', format: 'rgba16float' },
          { kind: 'texture', format: 'rgba16float' },
          { kind: 'storageTexture', format: 'rgba16float' },
          { kind: 'uniform' },
        ],
      },
      [
        fine,
        coarse,
        out,
        ctx.uniform(
          new Struct(32)
            .i(0, fine.width, fine.height)
            .i(8, coarse.width, coarse.height)
            .f(16, coarse.width / fine.width, coarse.height / fine.height)
            .u(24, level === 0 ? 1 : 0, 0).bytes,
        ),
      ],
      [workgroupCount(fine.width, 8), workgroupCount(fine.height, 8)],
      `alphafill:push#${level}`,
    );
    pyramid[level]!.release();
    pyramid[level] = out;
  }

  // Filled colour, original alpha.
  combine(ctx, pyramid[0]!, src, dst, COMBINE_RGB_ALPHA_A);

  for (const level of pyramid) level.release();
}
